import type { AutocaptureConfig } from '../types';
import type { MarkCore } from '../core/MarkCore';
import { onAfterPageChange, onBeforePageChange, onPageExit } from './pageLifecycle';
import { normalizePathnameForPageview } from './pageviewDedupeKey';
import { getMaxScrollPercent, resetPageMetrics, updateMaxScrollFromCurrentPosition } from './pageMetrics';

const DEFAULT_MIN_ACTIVE_MS = 10_000;

export type PageEngagementOptions = {
  minActiveMs: number;
  useBeacon: boolean;
  debug?: boolean;
};

export function resolvePageEngagementOptions(
  config: true | Exclude<AutocaptureConfig['page_engagement'], boolean | undefined>,
): PageEngagementOptions {
  if (config === true) {
    return { minActiveMs: DEFAULT_MIN_ACTIVE_MS, useBeacon: true };
  }
  return {
    minActiveMs: config.min_active_ms ?? DEFAULT_MIN_ACTIVE_MS,
    useBeacon: config.use_beacon !== false,
  };
}

export function installPageEngagement(client: MarkCore, options: PageEngagementOptions) {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  let pageStartedAt = Date.now();
  let activeTimeMs = 0;
  let lastVisibleAt: number | null = document.visibilityState === 'visible' ? Date.now() : null;
  let currentPage = normalizePathnameForPageview(window.location.pathname);
  let flushing = false;

  const pauseActiveTime = () => {
    if (lastVisibleAt === null) return;
    activeTimeMs += Date.now() - lastVisibleAt;
    lastVisibleAt = null;
  };

  const resumeActiveTime = () => {
    if (document.visibilityState === 'visible' && lastVisibleAt === null) {
      lastVisibleAt = Date.now();
    }
  };

  const syncVisibility = () => {
    if (document.visibilityState === 'visible') {
      resumeActiveTime();
    } else {
      pauseActiveTime();
    }
  };

  const flush = (preferBeacon: boolean) => {
    if (flushing) return;
    flushing = true;
    try {
      pauseActiveTime();
      updateMaxScrollFromCurrentPosition();
      if (activeTimeMs < options.minActiveMs) {
        if (options.debug) {
          console.debug('[Mark] page_engagement skipped (active time below min_active_ms)', {
            page: currentPage,
            active_time_ms: activeTimeMs,
            min_active_ms: options.minActiveMs,
          });
        }
        return;
      }
      const totalTimeMs = Date.now() - pageStartedAt;
      const payload = {
        page: currentPage,
        active_time_ms: activeTimeMs,
        total_time_ms: totalTimeMs,
        max_scroll_percent: getMaxScrollPercent(),
      };
      if (options.useBeacon && preferBeacon) {
        client.trackWithOptions('page_engagement', payload, { preferBeacon: true });
      } else {
        client.track('page_engagement', payload);
      }
    } finally {
      flushing = false;
    }
  };

  const startNewPage = () => {
    pageStartedAt = Date.now();
    activeTimeMs = 0;
    currentPage = normalizePathnameForPageview(window.location.pathname);
    resetPageMetrics();
    resumeActiveTime();
  };

  startNewPage();

  // One summary event per logical page: flush when navigating away or unloading.
  onBeforePageChange(() => {
    // SPA route changes: regular fetch is reliable; keepalive is for tab unload only.
    flush(false);
    pauseActiveTime();
  });

  onAfterPageChange(() => {
    startNewPage();
  });

  onPageExit(() => {
    flush(true);
  });

  // Tab backgrounding pauses the clock but does not end the page visit.
  document.addEventListener('visibilitychange', syncVisibility);
  window.addEventListener('pageshow', syncVisibility);
  window.addEventListener('focus', syncVisibility);
  // Catch visibility transitions that occur before listeners attach (common with async script load).
  queueMicrotask(syncVisibility);
  if (typeof requestAnimationFrame === 'function') {
    requestAnimationFrame(syncVisibility);
  }
}
